/**
 * F33 — admin trainer certification review + moderation API client, reshaped
 * by the F09 v2 F33 merge (plans/09_F09_verified_profiles.md §6 / §11).
 *
 * Endpoints:
 *   GET  /admin/trainer-verifications                       → pending-cert queue (FIFO)
 *   GET  /admin/trainer-verifications/:userId               → detail (profile + certs + presigned doc GET links)
 *   POST /admin/trainer-certifications/:certId/verify       → per-cert verify
 *   POST /admin/trainer-certifications/:certId/reject       → per-cert reject + reason
 *   POST /admin/trainer-certifications/:certId/revoke       → per-cert revoke + reason
 *   POST /admin/trainers/:userId/suspend                    → suspend trainer + reason
 *   POST /admin/trainers/:userId/reinstate                  → reinstate trainer
 *   POST /admin/trainers/:userId/force-deactivate           → force-deactivate profile + reason
 *
 * F09 v2 retired the F33 profile-level `POST /admin/trainer-verifications/
 * :userId/approve|reject` (they 404 now): the trainer tick is an F09 request
 * decided under `/verifications`. The queue is the pending-CERT queue — trainers
 * with at least one `pending` certification, oldest pending cert first — and
 * no longer takes a status filter. Rows and the detail carry the trainer tick
 * (`verified`, display-only `verifiedAt`); the detail adds the owner state.
 *
 * All gated by the admin permission `trainer_verifications:review` (super_admin
 * + content_moderator). Every mutation audit-logged server-side via F06's
 * onSend hook.
 *
 * Admin-only read shapes (`TrainerVerificationQueueItem`,
 * `TrainerVerificationForAdmin`, `AdminTrainerCertification`) are defined here
 * because they are not part of the frozen `@bravemove/types` public surface — they
 * compose the frozen `TrainerCertification` / `ReviewTrainerCertInput` exports
 * with admin-only review metadata + presigned doc links. This mirrors the F32
 * `AdminOpenGameDetailResponse` precedent.
 *
 * Component tests get the in-memory mock via the vitest alias in
 * `apps/admin/vitest.config.ts` rewriting `'./api/trainerVerifications'`.
 */
import type {
  AdminPageMeta,
  OwnerVerificationState,
  ReviewTrainerCertInput,
  TrainerCertification,
  TrainerCertificationStatus,
  TrainerLocationKind,
  TrainerSpecialty,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

// Re-export the frozen enums the pages consume so they can import them from
// this client module (and transparently from the fixture via the alias).
export type { ReviewTrainerCertInput, TrainerCertificationStatus } from '@bravemove/types';

// ─── Admin-only read shapes ──────────────────────────────────────────────────

/** A row in the FIFO pending-cert queue (trainers with ≥1 `pending` cert). */
export interface TrainerVerificationQueueItem {
  readonly userId: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly headline: string | null;
  /** F09 v2 — the trainer tick (`trainer_profiles.verification_status='verified'`). */
  readonly verified: boolean;
  /** Display-only "since" date; null when not verified. */
  readonly verifiedAt: string | null;
  /** Number of certs awaiting a per-doc decision (`status='pending'`). */
  readonly pendingCertCount: number;
  /** Total certs on the profile (any status). */
  readonly certCount: number;
  /** Upload time of the oldest pending cert — the FIFO key. */
  readonly oldestPendingCertAt: string | null;
  readonly suspendedAt: string | null;
  readonly deactivatedAt: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
}

/**
 * A certification as seen by an admin reviewer — includes the per-cert review
 * metadata + a short-lived presigned GET link for the private S3 doc. The link
 * issuance is logged server-side (`trainer-cert.doc_access`).
 */
export interface AdminTrainerCertification {
  readonly id: string;
  readonly title: string;
  readonly issuingBody: string | null;
  readonly issuedYear: number | null;
  readonly status: TrainerCertificationStatus;
  readonly fileName: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly sha256: string | null;
  readonly rejectionReason: string | null;
  readonly reviewedAt: string | null;
  /** Presigned GET URL (5-min expiry) for the cert doc, or null if unavailable. */
  readonly downloadUrl: string | null;
  readonly downloadUrlExpiresAt: string | null;
  readonly createdAt: string;
}

/** Trainer specialties projection used by the detail header. */
export type AdminTrainerSpecialty = TrainerSpecialty;

/** Full detail shown on the certification detail page. */
export interface TrainerVerificationForAdmin {
  readonly userId: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly roles: readonly string[];
  readonly headline: string | null;
  readonly bio: string | null;
  readonly yearsExperience: number | null;
  readonly languages: readonly string[];
  /** F09 v2 — the trainer tick. */
  readonly verified: boolean;
  /** Display-only "since" date; null when not verified. */
  readonly verifiedAt: string | null;
  /** F09 v2 — the derived owner state of the trainer tick. */
  readonly verificationState: OwnerVerificationState;
  readonly pendingCertCount: number;
  readonly oldestPendingCertAt: string | null;
  readonly adminNotes: string | null;
  readonly servesAffiliatedVenue: boolean;
  readonly servesClientVenue: boolean;
  readonly servesTrainerOwnLocation: boolean;
  readonly deactivatedAt: string | null;
  /** FIX 4.13 — set by an admin force-deactivate; only reinstate clears it. */
  readonly adminDeactivatedAt?: string | null;
  readonly suspendedAt: string | null;
  readonly suspensionReason: string | null;
  readonly specialties: readonly AdminTrainerSpecialty[];
  readonly certifications: readonly AdminTrainerCertification[];
  readonly countryCode: string;
  readonly createdAt: string;
}

/** Location modes offered, surfaced as chips on the detail page. */
export type AdminTrainerLocationKind = TrainerLocationKind;

// ─── Request bodies ──────────────────────────────────────────────────────────

export interface SuspendTrainerInput {
  readonly reason: string;
}
export interface ForceDeactivateTrainerInput {
  readonly reason: string;
}

// ─── Response envelope shapes ────────────────────────────────────────────────

export interface TrainerVerificationsListResponse {
  readonly items: readonly TrainerVerificationQueueItem[];
  readonly nextCursor: string | null;
  /** Present when `page` is supplied (page-numbered pagination). */
  readonly pagination?: AdminPageMeta;
}

export interface TrainerVerificationsListOpts {
  readonly limit?: number;
  /** 1-based page index (page-numbered pagination). */
  readonly page?: number;
  /** Page size (default 25 server-side, max 100). */
  readonly perPage?: number;
}

interface DataEnvelope<T> {
  readonly data: T;
}

// ─── API surface ─────────────────────────────────────────────────────────────

export const adminTrainerVerificationsApi = {
  /**
   * GET /admin/trainer-verifications — the FIFO pending-cert queue.
   *
   * The route returns `{ data: TrainerVerificationQueueItem[] }` (plus
   * `pagination` in page mode). Unwrap `data` into the `{ items, nextCursor }`
   * envelope the page expects; the route is not cursor-paginated, so
   * `nextCursor` is always null.
   */
  async list(opts: TrainerVerificationsListOpts = {}): Promise<TrainerVerificationsListResponse> {
    const qs = buildQuery({
      limit: opts.limit,
      page: opts.page,
      perPage: opts.perPage,
    });
    const body = await adminFetch<{
      data: readonly TrainerVerificationQueueItem[];
      pagination?: AdminPageMeta;
    }>(`/admin/trainer-verifications${qs}`);
    return {
      items: body.data ?? [],
      nextCursor: null,
      ...(body.pagination !== undefined ? { pagination: body.pagination } : {}),
    };
  },

  /** GET /admin/trainer-verifications/:userId — detail + presigned doc links. */
  async detail(userId: string): Promise<TrainerVerificationForAdmin | null> {
    try {
      // The route wraps the full detail in `{ data }`; unwrap it.
      const body = await adminFetch<DataEnvelope<TrainerVerificationForAdmin>>(
        `/admin/trainer-verifications/${encodeURIComponent(userId)}`,
      );
      return body.data;
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 404) return null;
      throw err;
    }
  },

  /** POST /admin/trainer-certifications/:certId/verify — per-cert verify. */
  /**
   * The three per-cert decisions reply the BARE `TrainerCertification`
   * (`TrainerCertificationService.decideCert`) — no `downloadUrl` /
   * `downloadUrlExpiresAt` (those only ride the detail's `getCertsForAdmin`
   * rows). Callers refetch the detail after a decision.
   */
  async verifyCert(certId: string): Promise<TrainerCertification> {
    const body = await adminFetch<DataEnvelope<TrainerCertification>>(
      `/admin/trainer-certifications/${encodeURIComponent(certId)}/verify`,
      { method: 'POST' },
    );
    return body.data;
  },

  /** POST /admin/trainer-certifications/:certId/reject — per-cert reject + reason. */
  async rejectCert(certId: string, input: ReviewTrainerCertInput): Promise<TrainerCertification> {
    const body = await adminFetch<DataEnvelope<TrainerCertification>>(
      `/admin/trainer-certifications/${encodeURIComponent(certId)}/reject`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  /** POST /admin/trainer-certifications/:certId/revoke — per-cert revoke + reason. */
  async revokeCert(certId: string, input: ReviewTrainerCertInput): Promise<TrainerCertification> {
    const body = await adminFetch<DataEnvelope<TrainerCertification>>(
      `/admin/trainer-certifications/${encodeURIComponent(certId)}/revoke`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  /** POST /admin/trainers/:userId/suspend — suspend trainer + reason. */
  async suspendTrainer(
    userId: string,
    input: SuspendTrainerInput,
  ): Promise<TrainerVerificationForAdmin> {
    const body = await adminFetch<DataEnvelope<TrainerVerificationForAdmin>>(
      `/admin/trainers/${encodeURIComponent(userId)}/suspend`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  /** POST /admin/trainers/:userId/reinstate — reinstate a suspended trainer. */
  async reinstateTrainer(userId: string): Promise<TrainerVerificationForAdmin> {
    const body = await adminFetch<DataEnvelope<TrainerVerificationForAdmin>>(
      `/admin/trainers/${encodeURIComponent(userId)}/reinstate`,
      { method: 'POST' },
    );
    return body.data;
  },

  /** POST /admin/trainers/:userId/force-deactivate — force-hide the profile. */
  async forceDeactivateTrainer(
    userId: string,
    input: ForceDeactivateTrainerInput,
  ): Promise<TrainerVerificationForAdmin> {
    const body = await adminFetch<DataEnvelope<TrainerVerificationForAdmin>>(
      `/admin/trainers/${encodeURIComponent(userId)}/force-deactivate`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },
};
